<script setup lang="ts">
import { useScroll } from '@vueuse/core'
import gsap from 'gsap'

const emit = defineEmits(['update:modelValue'])

const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  options: {
    type: Array as PropType<{ label: string; value: string }[]>,
    required: true
  },
  label: {
    type: String,
    default: 'Select option'
  },
  invertColors: {
    type: Boolean,
    default: false
  }
})

const selected = ref(1)
const isExpanded = ref(false)

const containerRef = ref<HTMLElement | null>(null)
const wrapperRef = ref<HTMLElement | null>(null)

const itemHeight = 38
const { isDesktop } = useDevice()

const visibleItems = ref(3)

const containerHeight = computed(() => itemHeight * visibleItems.value)
const contentHeight = computed(
  () => itemHeight * props.options.length + itemHeight * (visibleItems.value - 2)
)

const { y } = useScroll(containerRef)
const { bodyLock, bodyUnlock } = useBodyLock()

const scrollIndex = computed(() => {
  if (containerRef.value) {
    const topPosition = Math.abs(y.value) + itemHeight / 2
    return Math.floor(topPosition / itemHeight)
  }
  return 0
})

// Determine if the container is at the top or bottom
const isAtTop = computed(() => {
  return y.value <= 0
})

const isAtBottom = computed(() => {
  const maxScroll = containerRef.value?.scrollHeight - containerRef.value?.clientHeight || 0
  return Math.abs(y.value) >= maxScroll
})

const toggleExpansion = () => {
  if (isDesktop) return
  isExpanded.value = !isExpanded.value
}

const scrollUp = () => {
  if (!isAtTop.value && containerRef.value) {
    bodyLock()
    gsap.to(containerRef.value, {
      scrollTop: containerRef.value.scrollTop - itemHeight,
      duration: 0.4,
      ease: 'power2.out',
      onComplete: () => {
        bodyUnlock()
      }
    })
  }
}

const scrollDown = () => {
  if (!isAtBottom.value && containerRef.value) {
    bodyLock()

    gsap.to(containerRef.value, {
      scrollTop: containerRef.value.scrollTop + itemHeight,
      duration: 0.4,
      ease: 'power2.out',
      onComplete: () => {
        bodyUnlock()
      }
    })
  }
}

onClickOutside(wrapperRef, () => {
  if (isDesktop) return
  isExpanded.value = false
})

const throttledEmit = useThrottleFn((index: number) => {
  emit('update:modelValue', props.options[index]?.value)
}, 50)

const initScroll = ref(false)

watch(scrollIndex, (newIndex) => {
  if (!initScroll.value) {
    selected.value = newIndex + 1
    throttledEmit(newIndex + 1)
  }
})

const updateModelValue = (index: number) => {
  if (containerRef.value) {
    bodyLock()

    gsap.to(containerRef.value, {
      scrollTop: index * itemHeight,
      duration: 0.5,
      ease: 'power2.out',
      onComplete: () => {
        bodyUnlock()
      }
    })
  }
}

const scrollToSelected = useThrottleFn(() => {
  const selectedIndex = props.options.findIndex((option) => option.value === props.modelValue)
  if (selectedIndex !== -1 && containerRef.value) {
    initScroll.value = true
    bodyLock()
    gsap.to(containerRef.value, {
      scrollTop: (selectedIndex - 1) * itemHeight,
      duration: 0.5,
      ease: 'power2.out',
      onComplete: () => {
        bodyUnlock()
        initScroll.value = false
      }
    })

    selected.value = selectedIndex
    emit('update:modelValue', props.options[selectedIndex]?.value)
  }
}, 5)

onMounted(() => {
  useTimeoutFn(() => {
    scrollToSelected()
  }, 500)
})
</script>

<template>
  <div
    ref="wrapperRef"
    class="flex w-fit flex-col gap-2 text-left lg:flex-row lg:items-center lg:gap-4"
    :class="invertColors ? 'text-yellow' : 'text-black'"
    data-lenis-prevent
  >
    <span
      class="pl-2 text-lg uppercase transition-all lg:pl-0 lg:pt-2.5"
      :class="isExpanded && 'pb-[38px] lg:pb-0'"
      >{{ label }}</span
    >
    <div
      class="group relative -mt-[38px] min-h-12 max-w-[320px] text-left transition-all duration-300 sm:max-w-[380px] lg:mt-0"
      @click.stop="toggleExpansion"
      @mouseenter="isDesktop ? (isExpanded = true) : undefined"
      @mouseleave="isDesktop ? (isExpanded = false) : undefined"
    >
      <div
        class="absolute flex h-12 w-full flex-col items-end justify-between gap-0.5 rounded-xl border py-0.5 pr-1 transition-all duration-300 group-hover:top-0 group-hover:h-30 lg:top-[38px]"
        :class="[
          !isDesktop ? (isExpanded ? 'top-0 h-30' : 'top-[38px] h-12') : '',
          invertColors ? 'border-cream text-black' : 'border-black text-cream'
        ]"
      >
        <!-- Scroll Up Button -->
        <button
          :disabled="isAtTop"
          aria-label="Scroll up"
          type="button"
          class="relative z-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors duration-300"
          :class="[
            invertColors ? 'bg-yellow disabled:bg-yellow/20' : 'bg-black disabled:bg-black/30'
          ]"
          @click.prevent.stop="scrollUp"
        >
          <Icon name="custom:chevron-up-filled" size="16" />
        </button>

        <!-- Scroll Down Button -->
        <button
          :disabled="isAtBottom"
          aria-label="Scroll down"
          type="button"
          class="relative z-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors duration-300"
          :class="
            invertColors ? 'bg-yellow disabled:bg-yellow/20' : 'bg-black disabled:bg-black/30'
          "
          @click.prevent.stop="scrollDown"
        >
          <Icon name="custom:chevron-up-filled" size="16" class="rotate-180" />
        </button>
      </div>

      <div
        ref="containerRef"
        data-lenis-prevent
        class="relative w-fit min-w-[320px] snap-y overflow-y-auto scroll-smooth pl-5 pr-1 transition-all duration-300 [scrollbar-width:none]"
        :style="{ height: containerHeight + 'px', overflowY: 'auto' }"
      >
        <ul
          class="flex flex-col transition-all duration-300"
          :style="{ height: contentHeight + 'px' }"
        >
          <li
            v-for="(option, index) in options"
            :key="index"
            class="w-fit cursor-pointer snap-center pt-2.5 text-lg uppercase"
            :class="[
              !isExpanded ? (selected === index ? 'opacity-100' : 'opacity-0') : '',
              !isDesktop && !isExpanded && 'pointer-events-none'
            ]"
            :style="{ 'min-height': `${itemHeight}px` }"
            @click.prevent.stop="updateModelValue(index - 1)"
          >
            {{ option.label }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
